/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectorRef, Component, Inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import * as moment from 'moment';
import {AppEventBusService} from '../events/app-event-bus.service';

import {deferMarkForCheck} from '../util/defer-mark-for-check';
import {createBufferedFlag, readBufferedFlag} from '../util/template-state';
import template from './top-targeting-table.component.html';

import {OBJECT_TYPE} from '../constants/object-type';
import {StatsResource} from '../../../components/api/resources/resource/stats-resources/stats-resource';
import {Downloader} from '../../../components/util/downloader';
import {formatPercentage as formatPercentageValue} from '../filters/format-percentage';
import {AppLocaleService} from '../locale/app-locale.service';
import {OgTranslateService} from '../filters/og-translate.service';
import {AdnLogService} from '../../../components/util/adn-log.service';
import {ChartOptionsUtil} from '../options/chart-options-util.service';

@Component({
  standalone: false,
  selector: 'adn-top-targeting-table',
  template
})
export class TopTargetingTableComponent implements OnInit, OnChanges, OnDestroy {
  @Input() object: any;
  @Input() parentType: string;
  @Input() topN: any;
  @Input() view: string;
  @Input() dateSelectorId: string;
  @Input() currency: any;

  sel: any = {};
  semanticTargeting: any = null;
  semanticTargetingList: Array<{sentence: string; histogram: any[]; averageRenderedMatchScore?: number}> = [];
  sorting = {
    orderByParam: 'impressions',
    asc: true
  };

  parentParam: string;
  loaded = false;
  private readonly loadedFlag = createBufferedFlag();
  private destroyed = false;
  error = false;
  dataTotals: any[] = null;
  targetingExpression = false;
  readonly startDate = signal<any>('UNDEFINED');
  readonly endDate = signal<any>(undefined);
  canceller: any;

  readonly starScores = [1, 2, 3, 4, 5];

  private chartDateListener: () => void;
  private allFields: any[];
  private chartOptionObject: any;
  private params: any = {};
  private configured = false;

  get resolvedParentType(): string {
    return this.parentType;
  }

  get resolvedDateSelectorId(): string {
    return this.dateSelectorId;
  }

  get displayLoaded(): boolean {
    return readBufferedFlag(this.loadedFlag, this.loaded, this.cdr, () => !this.destroyed);
  }

  downloadFields: string[];
  downloadColumnOrder: string[];
  csvTableTitle: string;

  constructor(
    private readonly statsResource: StatsResource,
    private readonly eventBus: AppEventBusService,
    private readonly adnLog: AdnLogService,
    private readonly translate: TranslateService,
    private readonly ogTranslate: OgTranslateService,
    @Inject(ChartOptionsUtil) private readonly ChartOptionsUtil: any,
    private readonly appLocale: AppLocaleService,
    private readonly cdr: ChangeDetectorRef
  ) {
  }

  ngOnInit(): void {
    this.tryInit();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.parentType) {
      this.tryInit();
    }
  }

  private tryInit(): void {
    this.chartOptionObject = OBJECT_TYPE[this.resolvedParentType];
    if (!this.chartOptionObject) {
      return;
    }
    if (this.configured) {
      return;
    }
    this.configured = true;
    this.initParams();

    const chartOptions = this.ChartOptionsUtil.getOptions(this.chartOptionObject.caps);
    this.parentParam = this.chartOptionObject.apiParam;
    this.allFields = chartOptions.targetingFields;

    const eventName = 'chartDates' + chartOptions.idKey + (this.resolvedDateSelectorId || '');
    const applyChartDates = (data: any) => {
      if (this.startDate() !== 'UNDEFINED' && moment(this.startDate()).isSame(data.startDate) && moment(this.endDate()).isSame(data.endDate)) {
        return;
      }
      // Defer date bindings — lastDates / chartDates can land during parent CD
      // ('UNDEFINED' → Date NG0100 on [adnStartDate]).
      deferMarkForCheck(this.cdr, () => {
        if (this.startDate() !== 'UNDEFINED' && moment(this.startDate()).isSame(data.startDate) && moment(this.endDate()).isSame(data.endDate)) {
          return;
        }
        this.initParams();

        this.startDate.set(data.startDate);
        this.endDate.set(data.endDate);

        this.loaded = false;
        this.dataTotals = null;
        this.error = false;
        this.targetingExpression = false;

        this.params.groupBy = null;

        if (this.startDate() && this.endDate()) {
          this.params.startDate = this.startDate();
          this.params.endDate = this.endDate();
        }

        this.statsResource.getTargeting(this.params, (dataTotals: any) => {
          this.processTargetingData(dataTotals);
        }, (err: any) => {
          this.adnLog.warn(err);
          deferMarkForCheck(this.cdr, () => {
            this.error = true;
            this.loaded = true;
          });
        }, this);
      });
    };

    this.chartDateListener = this.eventBus.on(eventName, (_event: any, data: any) => {
      applyChartDates(data);
    });

    const lastDates = this.ChartOptionsUtil.getLastChartDates(eventName);
    if (lastDates) {
      applyChartDates(lastDates);
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.chartDateListener) {
      this.chartDateListener();
    }
    if (this.canceller) {
      this.canceller.resolve('Destroyed component');
    }
  }

  fieldTranslate(key: string): string {
    return this.ogTranslate.transform(key);
  }

  formatPercentage(input: number, decimals?: number): string {
    return formatPercentageValue(input, decimals, undefined, this.appLocale.getLocaleId());
  }

  asPercent(ratio: number): number {
    return (ratio || 0) * 100;
  }

  orderedHistogram(sentenceStats: any): any[] {
    return _.orderBy(sentenceStats?.histogram || [], 'score');
  }

  sortUpdate(field: any): void {
    if (field.key === this.sorting.orderByParam) {
      this.sorting.asc = !this.sorting.asc;
      return;
    }
    this.sorting.orderByParam = field.key;
    this.sorting.asc = true;
  }

  sortedDataTotals(): any[] {
    if (!this.dataTotals) {
      return [];
    }
    const key = this.sorting.orderByParam;
    // The old template's orderBy expression treated this flag as reverse.
    const reverse = this.sorting.asc;
    return _.orderBy(this.dataTotals, (row) => {
      const val = row[key];
      if (_.get(val, 'currency')) {
        return val.amount || -1;
      }
      if (_.get(val, 'count')) {
        return val.count || -1;
      }
      return val || -1;
    }, [reverse ? 'desc' : 'asc']);
  }

  toggleShowShort(dataRow: any): void {
    dataRow.showShort = !dataRow.showShort;
  }

  getRows(): any[] {
    return this.dataTotals;
  }

  saveXls(): void {
    this.updateValues();
    new Downloader().saveXls2(this.csvTableTitle, this.downloadFields, this.downloadColumnOrder, this.dataTotals || [], this.allFields);
  }

  saveDownloadFields(): void {
    this.updateValues();
  }

  private initParams(): void {
    if (!this.chartOptionObject || !this.object?.id) {
      return;
    }
    this.params = {
      id: this.object.id,
      timeout: 180000,
      idKey: this.chartOptionObject.apiParam + 'Id'
    };
  }

  private updateValues(): void {
    this.downloadFields = _.map(this.sel.fullFields, (f) => {
      return f.text || f.name;
    });
    this.downloadFields.unshift('Type', 'Targeting');
    this.downloadColumnOrder = _.map(this.sel.fullFields, (f) => {
      return f.key || f.id;
    });
    this.downloadColumnOrder.unshift('type', 'target');
    this.csvTableTitle = 'Targeting Totals';
  }

  private processTargetingData(data: any): void {
    let semanticTargeting: any = null;
    let targetingExpression = false;
    const dataTotals = _.map(data, (val, targeting) => {
      const expression = targeting.split('=');
      const targetType = expression[0];
      let targetDescription = expression.slice(1).join('=');
      const formattedData: any = {
        type: targetType
      };
      if (targetType === 'SEMANTIC_CATEGORY') {
        const sentence = targetDescription.split(':');
        if (sentence.length > 1) {
          const targetScore = sentence[0];
          const targetSentence = sentence[1];
          if (targetScore === 'SLIGHTLY_RELATED') {
            formattedData.score = 1;
          }
          if (targetScore === 'SOMEWHAT_RELATED') {
            formattedData.score = 2;
          }
          if (targetScore === 'MODERATELY_RELATED') {
            formattedData.score = 3;
          }
          if (targetScore === 'CLOSELY_RELATED') {
            formattedData.score = 4;
          }
          if (targetScore === 'VERY_CLOSELY_RELATED') {
            formattedData.score = 5;
          }
          if (formattedData.score || targetScore === 'NOT_RELATED' || targetScore === 'WEAKLY_RELATED') {
            targetDescription = sentence.slice(1).join(':');
          }

          semanticTargeting = semanticTargeting || {};
          semanticTargeting[targetSentence] = semanticTargeting[targetSentence] || {};
          semanticTargeting[targetSentence].histogram = semanticTargeting[targetSentence].histogram || [];
          semanticTargeting[targetSentence].histogram.push({
            score: formattedData.score,
            stats: val,
            sentence: targetSentence
          });
        }
      }
      formattedData.target = targetDescription;
      formattedData.targetShort = targetDescription.substring(0, 100);
      formattedData.targetLong = targetDescription.length >= 100;
      formattedData.showShort = formattedData.targetLong;
      if (formattedData.target) {
        targetingExpression = true;
      }
      _.forEach(val, (stat, keyStat) => {
        formattedData[keyStat] = stat;
      });
      return formattedData;
    });
    const sums = _.map(semanticTargeting, (sentenceStats, sentence) => {
      const sumRendered = _.reduce(sentenceStats.histogram, (num, smr) => {
        return num + smr.stats.rendered;
      }, 0);
      const sumRenderedMatchScore = _.reduce(sentenceStats.histogram, (num, histogramItem) => {
        return num + histogramItem.stats.renderedMatchScoreSum;
      }, 0);
      return {
        sentence: sentence,
        sumImpressions: _.reduce(sentenceStats.histogram, (num, histogramItem) => {
          return num + histogramItem.stats.impressions;
        }, 0),
        sumViewables: _.reduce(sentenceStats.histogram, (num, histogramItem) => {
          return num + histogramItem.stats.viewables;
        }, 0),
        sumRendered: sumRendered,
        sumClicks: _.reduce(sentenceStats.histogram, (num, histogramItem) => {
          return num + histogramItem.stats.clicks;
        }, 0),
        averageRenderedMatchScore: sumRendered === 0 ? 0 : (sumRenderedMatchScore / sumRendered) * 10 - 2
      };
    });
    _.forEach(semanticTargeting, (sentenceStats, sentence) => {
      const sum = _.find(sums, (s) => {
        return s.sentence === sentence;
      });
      if (sum) {
        _.forEach(sentenceStats.histogram, (histogramItem) => {
          histogramItem.ratioImpressions = histogramItem.stats.impressions / sum.sumImpressions;
          histogramItem.ratioViewables = histogramItem.stats.viewables / sum.sumViewables;
          histogramItem.ratioRendered = histogramItem.stats.rendered / sum.sumRendered;
          histogramItem.ratioClicks = histogramItem.stats.clicks / sum.sumClicks;
        });
        sentenceStats.averageRenderedMatchScore = sum.averageRenderedMatchScore;
      }
    });
    const semanticTargetingList = semanticTargeting
      ? _.map(semanticTargeting, (sentenceStats, sentence) => {
        return {
          sentence: sentence as string,
          histogram: sentenceStats.histogram,
          averageRenderedMatchScore: sentenceStats.averageRenderedMatchScore
        };
      })
      : [];
    deferMarkForCheck(this.cdr, () => {
      this.semanticTargeting = semanticTargeting;
      this.dataTotals = dataTotals;
      this.targetingExpression = targetingExpression;
      this.semanticTargetingList = semanticTargetingList;
      this.loaded = true;
    });
  }
}
